import { supabase } from '../supabaseClient';

// Compliance document EVENTS — the append-only version/decision spine behind
// the per-document detail page (document-detail-page brief §1/§6). This module
// is the ONLY place that writes/reads compliance_document_events, so the shape
// stays in one spot.
//
// Reconciliation: the CONVERSATION (admin asks / supplier replies) stays in
// compliance_clarification_messages. This table is uploads + decisions. The
// detail page merges the two by time — see loadDocumentStream.

// Log one event. Best-effort by default: a logging failure must never roll back
// the real action (an upload/approval already happened), so callers pass
// throwOnError only when they genuinely need to know.
export async function logComplianceEvent(evt, { throwOnError = false } = {}) {
  if (!evt?.supplierId || !evt?.documentKey || !evt?.eventType || !evt?.actorSide) {
    if (throwOnError) throw new Error('logComplianceEvent: missing required fields');
    return null;
  }
  const row = {
    supplier_id: evt.supplierId,
    document_key: evt.documentKey,
    event_type: evt.eventType,
    actor_side: evt.actorSide,
    actor_user_id: evt.actorUserId || null,
    body: evt.body || null,
    version: evt.version ?? null,
    file_path: evt.filePath || null,
    file_name: evt.fileName || null,
    attachment_url: evt.attachmentUrl || null,
    attachment_name: evt.attachmentName || null,
    meta: evt.meta || {},
  };
  const { data, error } = await supabase.from('compliance_document_events').insert(row).select('id').single();
  if (error) {
    if (throwOnError) throw new Error(error.message);
    // eslint-disable-next-line no-console
    console.warn('logComplianceEvent failed (non-fatal):', error.message);
    return null;
  }
  return data?.id || null;
}

// The next 1-based version number for a document — one more than the uploads
// already recorded for it. Best-effort: falls back to 1 on any error.
export async function nextDocumentVersion(supplierId, documentKey) {
  if (!supplierId || !documentKey) return 1;
  const { count, error } = await supabase
    .from('compliance_document_events')
    .select('id', { count: 'exact', head: true })
    .eq('supplier_id', supplierId)
    .eq('document_key', documentKey)
    .eq('event_type', 'uploaded');
  if (error || count == null) return 1;
  return count + 1;
}

// Load the full chronological stream for one document — the spine events merged
// with the conversation, newest-first. Each entry is normalised to a common
// shape so the detail page renders a single list:
//   { id, kind: 'event' | 'message', side: 'staff'|'supplier'|'system',
//     type, at, body, version, fileUrl, fileName, actorUserId }
export async function loadDocumentStream(supplierId, documentKey) {
  if (!supplierId || !documentKey) return [];
  const [evRes, msgRes] = await Promise.all([
    supabase.from('compliance_document_events')
      .select('*')
      .eq('supplier_id', supplierId)
      .eq('document_key', documentKey),
    supabase.from('compliance_clarification_messages')
      .select('*')
      .eq('supplier_id', supplierId)
      .eq('document_key', documentKey),
  ]);
  const events = (evRes.data || []).map(e => ({
    id: `ev-${e.id}`,
    kind: 'event',
    side: e.actor_side,
    type: e.event_type,
    at: e.created_at,
    body: e.body || null,
    version: e.version ?? null,
    fileUrl: e.file_path || e.attachment_url || null,
    fileName: e.file_name || e.attachment_name || null,
    actorUserId: e.actor_user_id || null,
    meta: e.meta || {},
  }));
  const messages = (msgRes.data || []).map(m => ({
    id: `msg-${m.id}`,
    kind: 'message',
    side: m.author === 'admin' ? 'staff' : 'supplier',
    type: 'message',
    at: m.created_at,
    body: m.body || null,
    version: null,
    fileUrl: m.attachment_url || null,
    fileName: m.attachment_name || null,
    actorUserId: m.created_by || null,
    meta: {},
  }));
  return [...events, ...messages].sort((a, b) => new Date(b.at) - new Date(a.at));
}

// ============================================================================
// The supplier's view of the same stream (detail-page brief §1, "both sides
// see it")
// ============================================================================
// Same events, but a supplier must never see the machinery behind a decision.
// Three things are internal and get dropped rather than reworded:
//
//   - spot-check and auto-approval HOLDS ('required' + meta.auto_approval) —
//     telling a supplier they were randomly sampled invites "why me?", and
//     telling them an auto-approval was held explains a mechanism they have no
//     part in
//   - nudge markers (meta.nudge) — the nudge itself reaches them by email; the
//     record of us deciding to send one is ours
//   - archive/unarchive — an internal filing decision about our own queue
//
// Everything else is theirs by right: what they uploaded, what we decided,
// what either side said. Labels are rewritten in the second person, because
// "Uploaded · Caroline's Petting Zoo" is how WE describe it, not how they'd
// read their own action.
const SUPPLIER_HIDDEN_TYPES = new Set(['archived', 'unarchived']);

export function supplierVisibleStream(stream) {
  return (stream || []).filter(item => {
    if (SUPPLIER_HIDDEN_TYPES.has(item.type)) return false;
    if (item.meta?.auto_approval) return false;   // spot check / held for review
    if (item.meta?.nudge) return false;           // our decision to chase them
    return true;
  });
}

// The headline a supplier should read for a stream entry. `kind` separates a
// message (rendered as a bubble) from a spine marker.
export function supplierEventLabel(item) {
  const mine = item.side === 'supplier';
  switch (item.type) {
    case 'uploaded':                return mine ? 'You uploaded this' : 'Document uploaded';
    case 'acknowledged':            return mine ? 'You acknowledged this' : 'Acknowledged';
    case 'approved':                return 'Approved by Fusion 2K';
    case 'expiry_approved':         return 'Expiry date confirmed';
    case 'rejected':                return 'Not accepted — a new copy is needed';
    case 'clarification_requested': return 'Fusion 2K asked for more information';
    case 'replied':                 return mine ? 'You replied' : 'Fusion 2K replied';
    case 'superseded':              return 'Replaced by a newer version';
    case 'required':                return 'Requested from you';
    default:                        return item.type;
  }
}

// All retained files for a document, one per version, newest-first — for the
// "every version's file stays downloadable" panel (§1).
export function versionFilesFromStream(stream) {
  return stream
    .filter(s => s.kind === 'event' && s.type === 'uploaded' && s.fileUrl)
    .sort((a, b) => (b.version || 0) - (a.version || 0) || new Date(b.at) - new Date(a.at));
}
